import { IAPIRoutes } from "../../../../config";

export type IClinicRepository = ReturnType<typeof ClinicRepositoryFactory>;

export const ClinicRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getClinicID: IGetMyClinicAPI = async () =>
    GET(
      APIRoutes.apiMyClinic,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getClinicFloor: IGetClinicFloorAPI = async (obj) =>
    GET(
      APIRoutes.apiGetClinicFloor.replace("{clinic_id}", String(obj.clinic_id)),
      obj,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const setClinicFloor: ISetClinicFloorAPI = async ({ clinic_id, ...obj }) =>
    POST(
      APIRoutes.apiSetClinicFloor.replace("{clinic_id}", clinic_id?.toString()),
      obj,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const logError: ILogErrorAPI = async (obj) =>
    POST(APIRoutes.apiMixPanelErrorStore, obj, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const fetchAssignedDeviceAPI: IFetchAssignedDeviceORAssingDeviceToAPatientAPI =
    async (obj) =>
      GET(
        APIRoutes.apiFetchAssignedDeviceORAssingDeviceToAPatient
          .replace("{clinic_id}", obj.clinic_id?.toString())
          .replace("{patient_id}", obj.patient_id?.toString()),
        obj,
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );

  const assingDeviceToAPatient: IAssingDeviceToAPatientAPI = async (obj) => {
    // For an UNASSIGN (status 0) the member endpoint expects ONLY `{ status: 0 }`
    // in the body — clinic_id / patient_id are already path params. Sending the
    // full payload (device_id/name/etc.) makes the backend respond with an error
    // even though it still unassigns the device. Assign (status 1) is unchanged:
    // it keeps sending the full payload the backend needs to create the link.
    const body = obj.status === 0 ? { status: 0 } : obj;
    return POST(
      APIRoutes.apiFetchAssignedDeviceORAssingDeviceToAPatient
        .replace("{clinic_id}", obj.clinic_id?.toString())
        .replace("{patient_id}", obj.patient_id?.toString()),
      body,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const deleteAccount: IDeleteAccountAPI = async (obj) =>
    DELETE(
      APIRoutes.apiDeleteAccount
        .replace("{clinic_id}", obj.clinic_id?.toString())
        .replace("{patient_id}", obj.patient_id?.toString()),
      obj,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const disconnectDevice: IDisconnectDeviceAPI = async (obj) =>
    POST(
      APIRoutes.apiAdminDisconnectDevice
        .replace("{clinic_id}", encodeURIComponent(String(obj.clinic_id)))
        .replace("{patient_id}", encodeURIComponent(String(obj.patient_id)))
        .replace("{device_id}", encodeURIComponent(obj.device_id)),
      // Endpoint takes no request body — pass an empty object so the
      // POST helper still serialises a JSON content-type header.
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const setDeviceWifi: ISetDeviceWifiAPI = async ({
    clinic_id,
    device_id,
    ...body
  }) =>
    POST(
      APIRoutes.apiSetDeviceWifi
        .replace("{clinic_id}", encodeURIComponent(String(clinic_id)))
        .replace("{device_id}", encodeURIComponent(device_id)),
      // clinic_id / device_id are path params — only the credential fields
      // (ssid, password, keep_existing, auth_type, username, restart_after_set)
      // go in the body.
      body,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  return {
    getClinicFloor,
    getClinicID,
    setClinicFloor,
    logError,
    fetchAssignedDeviceAPI,
    assingDeviceToAPatient,
    deleteAccount,
    disconnectDevice,
    setDeviceWifi,
  };
};
